/**
 * AdTech demographics / hashed-email enrichment dialog.
 *
 * Fetches demographic attributes (gender, age range, income, parent status, …)
 * and hashed email addresses for a set of AdTech registration ids from the
 * commercial provider's premium enrichment endpoints. Both collections may come
 * back empty if the tenant is not licensed for them — the dialog surfaces that
 * clearly rather than erroring.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import pathfinderApi from '../../services/pathfinderApi';

interface AdtechDemographicsDialogProps {
  open: boolean;
  registrationIds: string[];
  onClose: () => void;
}

interface DemoRow {
  registration_id: string;
  gender?: string;
  age_range?: string;
  marital_status?: string;
  education?: string;
  income?: string;
  generation?: string;
  is_parent?: string;
  children?: string;
  country?: string;
  hashed_email?: string;
}

// The upstream payload uses camelCase Venntel keys; normalize to a flat row and
// tolerate absent fields (unlicensed tenants return sparse objects).
const normalize = (
  demographics: Array<Record<string, any>>,
  emails: Record<string, string>,
): DemoRow[] => {
  const byId = new Map<string, DemoRow>();
  for (const d of demographics || []) {
    const rid = d.registrationID || d.registration_id || d.id || 'unknown';
    byId.set(rid, {
      registration_id: rid,
      gender: d.gender,
      age_range: d.ageRange || d.age_range,
      marital_status: d.maritalStatus || d.marital_status,
      education: d.educationLevel || d.education,
      income: d.annualIncome || d.income,
      generation: d.generationalSuffix || d.generation,
      is_parent: d.isAParent != null ? String(d.isAParent) : undefined,
      children: d.numberOfChildren != null ? String(d.numberOfChildren) : undefined,
      country: d.countryCode || d.country,
    });
  }
  // Fold in hashed emails, creating rows for ids present only in the email map.
  for (const [rid, email] of Object.entries(emails || {})) {
    const row = byId.get(rid) || { registration_id: rid };
    row.hashed_email = email;
    byId.set(rid, row);
  }
  return Array.from(byId.values());
};

const AdtechDemographicsDialog: React.FC<AdtechDemographicsDialogProps> = ({
  open,
  registrationIds,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<DemoRow[]>([]);

  const fetchData = useCallback(async () => {
    if (registrationIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const result = await pathfinderApi.getAdtechDemographics(registrationIds);
      setRows(normalize(result.demographics, result.hashed_emails));
    } catch (e: any) {
      setError(e?.message || 'Failed to fetch demographics');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [registrationIds]);

  useEffect(() => {
    if (open) fetchData();
    else { setRows([]); setError(null); }
  }, [open, fetchData]);

  const exportCsv = useCallback(() => {
    const headers = [
      'registration_id', 'gender', 'age_range', 'marital_status', 'education',
      'income', 'generation', 'is_parent', 'children', 'country', 'hashed_email',
    ];
    const csv = [
      headers.join(','),
      ...rows.map((r) => headers.map((h) => `"${String((r as any)[h] ?? '').replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `adtech-demographics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows]);

  const cell = (v?: string) => (v && v.trim() ? v : '—');
  const hasAny = rows.some((r) =>
    r.gender || r.age_range || r.income || r.marital_status || r.education || r.hashed_email);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 5 }}>
        <PersonSearchIcon fontSize="small" color="secondary" />
        Device Enrichment
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          ({registrationIds.length} device{registrationIds.length === 1 ? '' : 's'})
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 3, justifyContent: 'center' }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Querying commercial enrichment…</Typography>
          </Box>
        )}
        {!loading && error && (
          <Typography variant="body2" color="error" sx={{ py: 2 }}>{error}</Typography>
        )}
        {!loading && !error && rows.length > 0 && !hasAny && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No demographic attributes are available for these devices. This enrichment
            requires a premium data license; the provider returned no attributes.
          </Typography>
        )}
        {!loading && !error && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No enrichment data returned for the selected devices.
          </Typography>
        )}
        {!loading && !error && rows.length > 0 && (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Device', 'Gender', 'Age', 'Marital', 'Education', 'Income', 'Parent', 'Children', 'Country', 'Hashed Email'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.registration_id} hover>
                    <TableCell sx={{ fontSize: '0.72rem', maxWidth: 160 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={r.registration_id}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.registration_id.slice(0, 14)}…
                          </span>
                        </Tooltip>
                        <Tooltip title="Copy registration ID">
                          <IconButton size="small" onClick={() => navigator.clipboard?.writeText(r.registration_id)}>
                            <ContentCopyIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem' }}>
                      {r.gender ? <Chip label={r.gender} size="small" sx={{ height: 18, fontSize: '0.6rem' }} /> : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem' }}>{cell(r.age_range)}</TableCell>
                    <TableCell sx={{ fontSize: '0.72rem' }}>{cell(r.marital_status)}</TableCell>
                    <TableCell sx={{ fontSize: '0.72rem' }}>{cell(r.education)}</TableCell>
                    <TableCell sx={{ fontSize: '0.72rem' }}>{cell(r.income)}</TableCell>
                    <TableCell sx={{ fontSize: '0.72rem' }}>{cell(r.is_parent)}</TableCell>
                    <TableCell sx={{ fontSize: '0.72rem' }}>{cell(r.children)}</TableCell>
                    <TableCell sx={{ fontSize: '0.72rem' }}>
                      {r.country ? <Chip label={r.country} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} /> : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.68rem', maxWidth: 200 }}>
                      <Tooltip title={r.hashed_email || ''}>
                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                          {r.hashed_email || '—'}
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          startIcon={<DownloadIcon fontSize="small" />}
          disabled={rows.length === 0}
          onClick={exportCsv}
        >
          Export CSV
        </Button>
        <Button size="small" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdtechDemographicsDialog;
